import { Button, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import { useAppDispatch } from "../app/hooks";
import {
  setHideModalCloseButton,
  setModalContent,
  setShowModal
} from "./modalSlice";
import { useEffect } from "react";

type ConfirmNavProps = {
  title?: string;
  prompt: string;
  confirmText: string;
  cancelText: string;
  confirmAction: () => void;
  cancelAction: () => void;
};

export const ConfirmNav = ({
  title,
  prompt,
  confirmText,
  cancelText,
  confirmAction,
  cancelAction
}: ConfirmNavProps) => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={2} xs={12} p={2}>
      {title && (
        <Grid xs={12} alignItems="center">
          <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
            {t(title)}
          </Typography>
        </Grid>
      )}
      <Grid xs={12} alignItems="center">
        <Typography sx={{ textAlign: "center" }}>{t(prompt)}</Typography>
      </Grid>
      <Grid
        xs={12}
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={2}
      >
        <Button onClick={confirmAction} variant="contained">
          {t(confirmText)}
        </Button>
        <Button onClick={cancelAction} variant="outlined">
          {t(cancelText)}
        </Button>
      </Grid>
    </Grid>
  );
};

type UseBlockerWithConfirmationProps = {
  title?: string;
  prompt: string;
  confirmText: string;
  cancelText: string;
  shouldBlock: boolean;
};

export const useBlockerWithConfirmation = ({
  title,
  prompt,
  confirmText,
  cancelText,
  shouldBlock
}: UseBlockerWithConfirmationProps) => {
  const dispatch = useAppDispatch();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return shouldBlock && currentLocation.pathname !== nextLocation.pathname;
  });
  const hideModalAndResetCloseButton = () => {
    dispatch(setShowModal(false));
    dispatch(setHideModalCloseButton(false));
  };
  const confirmAction = () => {
    if (blocker.proceed) {
      hideModalAndResetCloseButton();
      blocker.proceed();
    }
  };
  const cancelAction = () => {
    if (blocker.reset) {
      hideModalAndResetCloseButton();
      blocker.reset();
    }
  };

  useEffect(() => {
    if (blocker.state === "blocked") {
      dispatch(
        setModalContent(
          <ConfirmNav
            title={title}
            prompt={prompt}
            confirmText={confirmText}
            cancelText={cancelText}
            confirmAction={confirmAction}
            cancelAction={cancelAction}
          />
        )
      );
      dispatch(setHideModalCloseButton(true));
      dispatch(setShowModal(true));
    }
  }, [blocker]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock) {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock]);
};
